import type { DestinationMonth } from '@/lib/data-source';
import { ui, uiFb } from '@/lib/i18n';
import { getMonthName } from '@/lib/constants';

const WEATHER_BAR_COLORS = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-lime-400', 'bg-green-400'];
const CROWD_ICONS: Record<string, string> = { low: '\u2713', medium: '\u25CB', high: '\u25CF' };

interface MonthConditionsChartProps {
  destMonths: DestinationMonth[];
  bestMonths: number[];
  lang: string;
}

export function MonthConditionsChart({ destMonths, bestMonths, lang }: MonthConditionsChartProps) {
  if (destMonths.length === 0) return null;

  return (
    <div className="mb-8 border-t border-border-subtle pt-6">
      <h2 className="font-heading text-xl font-bold text-text-primary md:text-2xl">
        {uiFb('ui.month_by_month_conditions', 'Month-by-Month Conditions', lang)}
      </h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[600px] text-xs">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="pb-2 text-left font-medium text-text-secondary">{uiFb('ui.month', 'Month', lang)}</th>
              <th className="pb-2 text-center font-medium text-text-secondary">{uiFb('ui.weather', 'Weather', lang)}</th>
              <th className="pb-2 text-center font-medium text-text-secondary">{uiFb('ui.crowds', 'Crowds', lang)}</th>
              <th className="pb-2 text-center font-medium text-text-secondary">{uiFb('ui.prices', 'Prices', lang)}</th>
              <th className="pb-2 text-left font-medium text-text-secondary">{uiFb('ui.temp', 'Temp', lang)}</th>
              <th className="pb-2 text-left font-medium text-text-secondary">{uiFb('ui.season', 'Season', lang)}</th>
            </tr>
          </thead>
          <tbody>
            {destMonths.sort((a, b) => a.month - b.month).map(dm => {
              const isPeak = bestMonths.includes(dm.month);
              return (
                <tr key={dm.month} className={`border-b border-border-subtle/50 ${isPeak ? 'bg-accent-primary/5' : ''}`}>
                  <td className="py-2 font-medium text-text-primary">
                    {getMonthName(dm.month - 1, lang).slice(0, 3)}
                    {isPeak && <span className="ml-1 text-accent-primary">*</span>}
                  </td>
                  <td className="py-2 text-center">
                    <div className="mx-auto flex h-3 w-20 overflow-hidden rounded-full bg-surface-root">
                      <div className={`h-full rounded-full ${WEATHER_BAR_COLORS[dm.weatherScore]}`} style={{ width: `${dm.weatherScore * 20}%` }} />
                    </div>
                  </td>
                  <td className="py-2 text-center">
                    <span className="capitalize">{CROWD_ICONS[dm.crowdLevel]} {dm.crowdLevel}</span>
                  </td>
                  <td className="py-2 text-center capitalize">{dm.priceLevel}</td>
                  <td className="py-2 text-text-secondary">{dm.tempRange}</td>
                  <td className="py-2 text-text-secondary">{dm.seasonName}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-caption text-text-secondary">{uiFb('ui.best_months_note', '* Best months to visit', lang)}</p>
    </div>
  );
}
